import {
  APIComponentAttributes,
  APIModelAttributes,
  ArdoqId,
  FilterInterfaceFilter,
  FormattingFilter,
  GroupingRule,
  isComponent as isComponentAPICheck,
  isComponentLabelFormatting,
  LinkedComponent,
  WorkspaceIntegration,
} from '@ardoq/api-types';
import {
  getFillAndStroke,
  sanitizeFilterColorClassName,
} from '@ardoq/color-helpers';
import {
  classes,
  compareComponentsById,
  ComponentByIdSortDependencies,
  ContextSort,
  isTruthy,
} from '@ardoq/common-helpers';
import { toId, toIds } from '@ardoq/core';
import {
  ComponentLabelSource,
  EnhancedScopeData,
  GetCssClassNamesOption,
} from '@ardoq/data-model';
import { dateRangeOperations } from '@ardoq/date-range';
import { LI_BACKGROUND_CLASS_NAME } from '@ardoq/global-consts';
import type { I18nLocale } from '@ardoq/i18n';
import { Locale } from '@ardoq/locale';
import { enhancedScopeDataOperations } from './enhancedScopeDataOperations';
import * as fieldUtils from './fieldUtils';
import { getFieldsByType, type FormatUserFieldFn } from './fieldUtils';
import type { FilterColorData } from './filterColorDataOperations';
import { filterColorDataOperations } from './filterColorDataOperations';
import { resolveFilterColor } from './filterColorUtils';
import * as groupingUtils from './groupingUtils';
import { getDescendantIdsFromHierarchy } from './internal/componentHierarchy';
import * as workspaceUtils from './workspaceUtils';
import { getAttribute as getWorkspaceAttribute } from './workspaceUtils';

import {
  ENTITY_CSS_KEY_PREFIX,
  entityCssOperations,
} from './entityCssOperations';

export const getAttribute = (
  scopeData: EnhancedScopeData,
  componentId: ArdoqId,
  attribute: keyof APIComponentAttributes
) => {
  const component = scopeData.componentsById[componentId];
  // TODO: `null` has for some fields a meaning, so we should rather return
  // `undefined` here
  return component ? component[attribute] : null;
};

export const getWorkspaceId = (
  scopeData: EnhancedScopeData,
  componentId: ArdoqId
) => {
  const component = scopeData.componentsById[componentId];
  if (!component) return null;
  return component.rootWorkspace;
};

export const getType = (scopeData: EnhancedScopeData, componentId: ArdoqId) => {
  const component = scopeData.componentsById[componentId];
  if (!component) return null;
  return enhancedScopeDataOperations.getComponentTypeByComponentId(
    scopeData,
    componentId
  );
};

export const getParentId = (
  scopeData: EnhancedScopeData,
  componentId: ArdoqId
) => {
  const component = enhancedScopeDataOperations.getComponent(
    scopeData,
    componentId
  );
  return component?.parent || null;
};

/**
 * Scope data always includes the component's full ancestor chain, but sometimes (in viewpoint mode)
 * we only want to return the parent id if the corresponding component is included into the viewpoint
 * or is affected by parent grouping.
 */
export const getParentIdIfInScope = (
  scopeData: EnhancedScopeData,
  componentId: ArdoqId,
  groupBys: GroupingRule[] = []
) => {
  const parentId = getParentId(scopeData, componentId);
  if (!parentId) return null;

  const isParentInScopeComponents =
    enhancedScopeDataOperations.isScopeComponent(scopeData, parentId);
  if (isParentInScopeComponents) {
    return parentId;
  }

  const isParentIncludedByParentGrouping =
    groupingUtils.isIncludedInScopeByParentGroupingRule(
      scopeData,
      parentId,
      groupBys
    );

  return isParentIncludedByParentGrouping ? parentId : null;
};

export const getLevel = (
  scopeData: EnhancedScopeData,
  componentId: ArdoqId
): number => {
  if (!enhancedScopeDataOperations.getComponent(scopeData, componentId)) {
    return -1;
  }
  let level = 1;
  let parentId = getParentId(scopeData, componentId);
  while (
    parentId &&
    enhancedScopeDataOperations.getComponent(scopeData, parentId)
  ) {
    parentId = getParentId(scopeData, parentId);
    level++;
  }
  return level;
};

export const getTypeId = (
  scopeData: EnhancedScopeData,
  componentId: ArdoqId
) => {
  return (
    enhancedScopeDataOperations.getComponentTypeByComponentId(
      scopeData,
      componentId
    )?.id ?? null
  );
};

export const getLabel = (
  scopeData: EnhancedScopeData,
  filters: (FilterInterfaceFilter | FormattingFilter)[],
  componentId: ArdoqId,
  i18nLocale: I18nLocale,
  formatUserFieldFn?: FormatUserFieldFn
) => {
  const component = scopeData.componentsById[componentId];
  if (!component) return null;

  const labelFormattingFilter = filters.find(isComponentLabelFormatting);
  if (!labelFormattingFilter) return component.name;

  const { value: formattingFieldKey, includeTime } = labelFormattingFilter;
  switch (formattingFieldKey) {
    case ComponentLabelSource.NAME:
      return component.name;
    case ComponentLabelSource.TYPE:
      return (
        enhancedScopeDataOperations.getComponentTypeByComponentId(
          scopeData,
          componentId
        )?.name ?? null
      );
    case ComponentLabelSource.NONE:
      return null;
    default: {
      const field = getFieldsByType(scopeData, {
        typeId: component.typeId,
        modelId: component.model,
      })[formattingFieldKey];
      if (!field) return component.name;

      const formattedValue = fieldUtils.format({
        fieldId: field._id,
        value: component[formattingFieldKey],
        scopeData,
        i18nLocale,
        excludeTime: !includeTime,
        formatUserFieldFn,
      });
      if (!formattedValue) return component.name;

      return `${component.name} [${field.label}: ${formattedValue}]`;
    }
  }
};

export const isComponent = (
  scopeData: EnhancedScopeData,
  componentId: ArdoqId
) => {
  const component = scopeData.componentsById[componentId];
  return isComponentAPICheck(component);
};

export const hasURLFields = (
  scopeData: EnhancedScopeData,
  componentId: ArdoqId
) => {
  const component = scopeData.componentsById[componentId];
  if (!component) return false;
  const modelIdAndTypeId = `${component.model}-${component.typeId}`;
  const fieldsByName = scopeData.fieldsByModelIdAndTypeId[modelIdAndTypeId];
  if (!fieldsByName) return false;
  return Object.values(fieldsByName).some(({ type }) => type === 'Url');
};

export const getChildren = (
  scopeData: EnhancedScopeData,
  componentId: ArdoqId
): APIComponentAttributes[] => {
  const workspaceId = getWorkspaceId(scopeData, componentId);

  if (!workspaceId) return [];

  return (scopeData.hierarchy.get(workspaceId)?.get(componentId) ?? [])
    .map(id => scopeData.componentsById[id])
    .filter(isTruthy);
};

export const getChildrenIds = (
  scopeData: EnhancedScopeData,
  componentId: ArdoqId
): ArdoqId[] => toIds(getChildren(scopeData, componentId));

export const getDescendantIds = (
  scopeData: EnhancedScopeData,
  componentId: ArdoqId
) => {
  const workspaceId = getWorkspaceId(scopeData, componentId) ?? '';
  const hierarchy = scopeData.hierarchy.get(workspaceId) ?? new Map();
  return getDescendantIdsFromHierarchy(hierarchy, componentId);
};

export const getChildrenIdsInScope = (
  scopeData: EnhancedScopeData,
  componentId: ArdoqId
) =>
  getChildrenIds(scopeData, componentId).filter(id =>
    enhancedScopeDataOperations.isScopeComponent(scopeData, id)
  );

export const getComponentAncestryIds = (
  scopeData: EnhancedScopeData,
  componentId?: ArdoqId | null
): ArdoqId[] =>
  componentId
    ? [
        ...getComponentAncestryIds(
          scopeData,
          getParentId(scopeData, componentId)
        ),
        componentId,
      ]
    : [];

export const getComponentIdsWithAncestors = (
  scopeData: EnhancedScopeData,
  componentIds: ArdoqId[]
): ArdoqId[] => [
  ...new Set(
    componentIds.flatMap(componentId =>
      getComponentAncestryIds(scopeData, componentId)
    )
  ),
];

export const belongsToFlexibleModel = (
  scopeDate: EnhancedScopeData,
  componentId: ArdoqId
): boolean => {
  const rootWorkspace = scopeDate.componentsById[componentId]?.rootWorkspace;
  return rootWorkspace
    ? workspaceUtils.isFlexibleWorkspace(scopeDate, rootWorkspace)
    : false;
};

export const getModelOfComponent = (
  scopeData: EnhancedScopeData,
  componentId: ArdoqId
): APIModelAttributes | null => {
  const workspaceId = getWorkspaceId(scopeData, componentId);
  return workspaceId
    ? workspaceUtils.getWorkspaceModel(scopeData, workspaceId)
    : null;
};

export const canComponentHaveChildren = (
  scopeData: EnhancedScopeData,
  componentId: ArdoqId
): boolean => {
  const model = getModelOfComponent(scopeData, componentId);
  if (!model) return false;
  if (model.flexible) return true;
  const type = enhancedScopeDataOperations.getComponentTypeByComponentId(
    scopeData,
    componentId
  );
  return type ? Object.keys(type.children).length > 0 : false;
};

export const getSortedComponentChildrenIds = (
  scopeData: EnhancedScopeData,
  componentId: ArdoqId,
  sort: ContextSort,
  userLocale: Locale
): ArdoqId[] => {
  const childrenIds = getChildrenIds(scopeData, componentId);
  const sortDependencies: ComponentByIdSortDependencies = {
    componentAttributesById: scopeData.componentsById,
    sort,
    isDateRangeField: dateRangeOperations.isCombinedDateRangeFieldNames(
      sort.attr
    ),
    userLocale,
  };
  return childrenIds.sort((aId, bId) =>
    compareComponentsById(aId, bId, sortDependencies)
  );
};

// Base color resolution without formatting filters.
// For the full display color including conditional formatting, use getComponentDisplayColorAsSVGAttributes.
export const getComponentColorAsSVGAttributes = (
  enhancedScopeData: EnhancedScopeData,
  componentId: ArdoqId,
  { useAsBackgroundStyle }: GetCssClassNamesOption
): { fill?: string; stroke?: string } => {
  const baseColor = enhancedScopeDataOperations.getComponentColor(
    enhancedScopeData,
    componentId
  );
  if (!baseColor) {
    return { fill: undefined, stroke: undefined };
  }
  return getFillAndStroke(baseColor, useAsBackgroundStyle);
};

/**
 * Returns the final display fill/stroke SVG attributes for a component,
 * accounting for conditional formatting (filter colors).
 *
 * Pure replacement for componentInterface.getComponentDisplayColorAsSVGAttributes.
 */
export const getComponentDisplayColorAsSVGAttributes = (
  enhancedScopeData: EnhancedScopeData,
  filterColorData: FilterColorData,
  componentId: ArdoqId,
  options: GetCssClassNamesOption
): { fill?: string; stroke?: string } => {
  const filterEntry = filterColorDataOperations.getComponentFilterColor(
    filterColorData,
    componentId
  );
  if (filterEntry) {
    return resolveFilterColor(filterEntry.color, filterEntry.version, options);
  }

  if (enhancedScopeDataOperations.isComponent(enhancedScopeData, componentId)) {
    return getComponentColorAsSVGAttributes(
      enhancedScopeData,
      componentId,
      options
    );
  }

  // We support calling this function for non-component nodes for compatibility with the componentInterface version,
  // but at some point we might want to remove this support and rather clean up the call stacks
  return { fill: undefined, stroke: undefined };
};

type GetComponentCSSClassNamesArgs = {
  scopeData: EnhancedScopeData;
  componentId: ArdoqId;
  options?: GetCssClassNamesOption;
  filterColorData?: FilterColorData;
};

const getFilterColorClassName = (
  filterColorData: FilterColorData,
  componentId: ArdoqId
): string => {
  const entry = filterColorDataOperations.getComponentFilterColor(
    filterColorData,
    componentId
  );
  if (!entry) return '';
  return sanitizeFilterColorClassName(entry.color);
};

export const getComponentCSSClassNames = ({
  scopeData,
  componentId,
  options = {
    useAsBackgroundStyle: false,
    includeLevels: true,
  },
  filterColorData,
}: GetComponentCSSClassNamesArgs) => {
  const component = scopeData.componentsById[componentId];

  if (!component) return null;

  let levelCss = '';
  if (options.includeLevels) {
    const compType = enhancedScopeDataOperations.getComponentTypeByComponentId(
      scopeData,
      componentId
    );
    const index = compType ? compType.index : 1;

    let level = 1;
    let p = component.parent
      ? scopeData.componentsById[component.parent]
      : null;
    while (p) {
      p = p.parent ? scopeData.componentsById[p.parent] : null;
      level++;
    }
    levelCss = `level${level} subLevel${index}`;
  }

  const entityClassName = entityCssOperations.getEntityCSSKey(component);

  const filterColorClass = filterColorData
    ? getFilterColorClassName(filterColorData, componentId)
    : '';

  return classes(
    'component',
    entityClassName,
    levelCss,
    component.typeId,
    component['component-key'],
    filterColorClass,
    options.useAsBackgroundStyle && LI_BACKGROUND_CLASS_NAME
  );
};

/**
 * Variant of {@link getComponentCSSClassNames} for contexts that only have a component id and its
 * type id, with no scopeData to look the component up in. Produces the core
 * `component id_<componentId> <typeId>` class shape; levels, component-key, and filter colors are
 * omitted as they require scopeData.
 */
export const getComponentCSSClassNamesFromTypeId = ({
  componentId,
  typeId,
}: {
  componentId: ArdoqId;
  typeId: ArdoqId;
}): string =>
  classes('component', `${ENTITY_CSS_KEY_PREFIX}${componentId}`, typeId);

const getManagedStatesForComponent = (
  enhancedScopeData: EnhancedScopeData,
  componentId: ArdoqId
): { id: ArdoqId; seenInLastImport: boolean }[] => {
  const rootWorkspace = getAttribute(
    enhancedScopeData,
    componentId,
    'rootWorkspace'
  );
  if (!rootWorkspace) {
    return [];
  }
  const jobIds = (
    getWorkspaceAttribute(enhancedScopeData, rootWorkspace, 'integrations') ??
    []
  ).map(toId);

  const ardoq = getAttribute(enhancedScopeData, componentId, 'ardoq');

  const managedData = ardoq?.persistent?.managed || {};
  return jobIds.filter(id => managedData[id]).map(id => managedData[id]);
};

export const isExternallyManaged = (
  scopeDate: EnhancedScopeData,
  componentId: ArdoqId
): boolean => {
  const activeManagedDefinitions = getManagedStatesForComponent(
    scopeDate,
    componentId
  );
  return Boolean(
    activeManagedDefinitions &&
    activeManagedDefinitions.find(x => x.seenInLastImport)
  );
};

export const isFieldManagedByWorkspaceIntegrations = (
  integrations: WorkspaceIntegration[] | undefined,
  fieldName: string
): boolean =>
  Boolean(integrations?.flatMap(i => i.componentFields).includes(fieldName));

export const isFieldExternallyManagedForIntegrations = (
  integrations: WorkspaceIntegration[] | undefined,
  component: APIComponentAttributes,
  fieldName: string
): boolean => {
  if (!integrations?.flatMap(i => i.componentFields).includes(fieldName))
    return false;

  const jobIds = integrations.map(toId);

  const managedData = component.ardoq?.persistent?.managed || {};
  return jobIds
    .map(jobId => managedData[jobId])
    .filter(Boolean)
    .some(x => x.seenInLastImport);
};

export const isFieldExternallyManaged = (
  scopeData: EnhancedScopeData,
  componentId: ArdoqId,
  fieldName: string
): boolean => {
  const component = scopeData.componentsById[componentId];
  if (!component) return false;
  const workspace = scopeData.workspacesById[component.rootWorkspace];
  if (!workspace) return false;

  return isFieldExternallyManagedForIntegrations(
    workspace.integrations,
    component,
    fieldName
  );
};

export const isExternallyMissing = (
  scopeDate: EnhancedScopeData,
  componentId: ArdoqId
): boolean => {
  const activeManagedDefinitions = getManagedStatesForComponent(
    scopeDate,
    componentId
  );
  return Boolean(
    activeManagedDefinitions &&
    activeManagedDefinitions.length > 0 &&
    activeManagedDefinitions.every(x => !x.seenInLastImport)
  );
};

export const getSources = (
  scopeData: EnhancedScopeData,
  componentId: ArdoqId
): LinkedComponent[] => scopeData.graphData.targetMap.get(componentId) || [];

export const getTargets = (
  scopeData: EnhancedScopeData,
  componentId: ArdoqId
): LinkedComponent[] => scopeData.graphData.sourceMap.get(componentId) || [];

export const getRootComponents = (
  scopeData: EnhancedScopeData,
  workspaceId: ArdoqId
): APIComponentAttributes[] =>
  scopeData.components.filter(
    component =>
      component.rootWorkspace === workspaceId && component.parent === null
  );

/**
 * Checks if a component is scenario-related: connected to the scenario but not in the scenario's scope.
 *
 * **Reasoning:** In scenarios, we distinguish three sets of components:
 * 1. Scope components - explicitly selected for the scenario
 * 2. Context components - added automatically (e.g., parents for hierarchy consistency)
 * 3. **Scenario-related components** - connected via references but outside the scenario scope
 *
 * This function identifies set #3. These components appear in `connected.components` (one hop away)
 * but not in `components` (scope + context). This distinction enables UIs to show connected
 * components differently (e.g., grayed out or with special styling).
 *
 * Matches the logic from `setDefaultCollectionView()` in scope/utils.tsx:174-177.
 *
 * **Usage:**
 * ```typescript
 * const loadedScenarioData: LoadedScenarioData = getScenarioData();
 * const { components, connected } = loadedScenarioData.scenarioData;
 * const componentIds = new Set(components.map(c => c._id));
 * const connectedIds = new Set(connected.components.map(c => c._id));
 * const isRelated = isComponentScenarioRelated(id, componentIds, connectedIds);
 * ```
 *
 * @param componentId - Component ID to check
 * @param loadedScenarioDataComponents - Set of component IDs from scenarioData.components (scope + context)
 * @param loadedScenarioDataConnectedComponents - Set of component IDs from scenarioData.connected.components
 * @returns true if component is connected to but outside the scenario scope
 */
export const isComponentScenarioRelated = (
  componentId: ArdoqId,
  loadedScenarioDataComponents: Set<ArdoqId>,
  loadedScenarioDataConnectedComponents: Set<ArdoqId>
): boolean =>
  loadedScenarioDataConnectedComponents.has(componentId) &&
  !loadedScenarioDataComponents.has(componentId);
